/**
 * Schemas for the `news.*` tRPC procedures.
 *
 * Cursor pagination uses the integer primary key as a stable, indexable
 * cursor. The frontend hook (`useInfiniteQuery`) passes the cursor back
 * verbatim; the router validates it as a non-negative integer.
 *
 * Locale resolution: the router prefers the `headlineLocaleEn|Ar|Fr`
 * column matching `ctx.locale` and falls back to the canonical
 * `headline` column when the localised value is null.
 */

import { z } from "zod";

export const NEWS_SEVERITIES = ["info", "warning", "urgent"] as const;
export const newsSeveritySchema = z.enum(NEWS_SEVERITIES);
export type NewsSeverity = z.infer<typeof newsSeveritySchema>;

export const newsItemSchema = z.object({
  id: z.number().int(),
  outbreakId: z.number().int().nullable(),
  source: z.string(),
  sourceUrl: z.string().url(),
  /** Locale-resolved headline. */
  headline: z.string(),
  summary: z.string().nullable(),
  publishedAt: z.date(),
  severity: newsSeveritySchema,
  /** When the item was fetched by the scraper — serves as the verification timestamp. */
  verifiedAt: z.date().nullable(),
});
export type NewsItem = z.infer<typeof newsItemSchema>;

export const newsListInputSchema = z.object({
  /** Restrict to a single outbreak. Omit for site-wide feed. */
  slug: z.string().min(1).max(100).optional(),
  /** Last id seen by the client. */
  cursor: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(100).default(20),
});
export type NewsListInput = z.infer<typeof newsListInputSchema>;

export const newsListOutputSchema = z.object({
  items: z.array(newsItemSchema),
  nextCursor: z.number().int().min(0).nullable(),
});
export type NewsListOutput = z.infer<typeof newsListOutputSchema>;

export const newsGetByIdInputSchema = z.object({
  id: z.number().int().min(0),
});
